import React from 'react';
import ReactDOM from 'react-dom';
import router from 'lib/router';
import routeUtils from 'util/routeUtils';
import {FormattedMessage, intlShape, injectIntl} from 'react-intl';
import IntlWrapper from 'components/layout/intlWrapper';
import events from 'lib/events';
import Loader from 'components/loader';
import Header from 'components/layout/header';
import LayoutSelect from 'components/forms/layoutSelect';
import {
  trashBinRecordTypesFormat,
  trashBinTimeFramesFormat,
  trashBinRecordTypes,
} from 'helpers/constants';
import PageFooter from 'components/layout/pageFooter.jsx';
import Pagination from 'components/layout/pagination.jsx';
import TrashBinApi from 'api/trashBin.jsx';
import TrashBinErrorHandler from './TrashBin/TrashBinErrorHandler.jsx';
import TrashBinRecord from './TrashBin/TrashBinRecord';
import TrashBinModel from 'models/trashBin.jsx';
import dealerApi from 'api/dealer';
import session from 'services/sessionService';
import ConfirmationModal from 'components/layout/ConfirmationModal';

const PAGE_SIZE = 10;

let render = (container, params) => {
  console.info('[UI.page.trashbin] rendering page');
  ReactDOM.render(
    <IntlWrapper>
      <WrapperTrashBin />
    </IntlWrapper>,
    container
  );
  events.publish('page-change', {page: 'tools'});
};

console.info('[UI.page.tools] register route');
router.rr.add([
  {path: 'tools/trashbin', handler: routeUtils.requireAuth(render)},
]);

class TrashBin extends React.Component {
  static propTypes = {
    intl: intlShape.isRequired,
  };

  state = {
    loaded: false,
    page: 1,
    total: 0,
    report: [],
    recordType: null,
    timeframe: 90,
    noDataMessage: false,
    error: false,
    dealer: null,
    confirmVisible: false,
    recordToRestore: null,
  };

  componentDidMount() {
    dealerApi
      .getDealer(session.getUser().dealerId)
      .then(dealer => this.setState({dealer}));
    this.fetchData();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.recordType !== this.state.recordType ||
      prevState.timeframe !== this.state.timeframe
    ) {
      this.fetchData();
    }
  }

  fetchData = () => {
    this.setState({
      report: [],
      loaded: false,
      error: false,
      confirmVisible: false,
      recordToRestore: null,
    });

    TrashBinApi.getList(this.state.recordType, this.state.timeframe, this.state.page, PAGE_SIZE)
      .then(data => {
        if (data && data.total) {
          this.setState({
            loaded: true,
            noDataMessage: false,
            page: data.page,
            report: data.report.map(d => TrashBinModel.fromApiFormat(d)),
            total: data.total,
          });
        } else {
          this.setState({
            noDataMessage: true,
            loaded: true,
          });
        }
      })
      .catch(err => {
        console.log(err);
        this.setState({
          noDataMessage: true,
          loaded: true,
          error: true,
        });
      });
  };

  updateFilter = ({key, value}) => {
    this.setState({[key]: value});
  };

  buildDropdownOptions = (formattedConstantsOptions, addAll = false) => {
    const {formatMessage} = this.props.intl;

    const options = Object.keys(formattedConstantsOptions).map(function(key) {
      return {value: key, label: formattedConstantsOptions[key]};
    });

    if (addAll) {
      options.unshift({
        value: null,
        label: formatMessage({id: 'general.all'}),
      });
    }

    return {
      labels: options.map(o => o.label),
      keys: options.map(o => o.value),
    };
  };

  changePage = page => {

    this.setState({
      loaded: false,
      error: false,
    });

    TrashBinApi.getList(this.state.recordType, this.state.timeframe, page, PAGE_SIZE)
    .then(data => {
      if (data && data.total) {
        this.setState({
          loaded: true,
          noDataMessage: false,
          page: data.page,
          report: data.report.map(d => TrashBinModel.fromApiFormat(d)),
          total: data.total,
        });
      } else {
        this.setState({
          noDataMessage: true,
          loaded: true,
        });
      }
    })
    .catch(err => {
      console.log(err);
      this.setState({
        noDataMessage: true,
        loaded: true,
        error: true,
      });
    });
  };

  getRecordID = r => {
    switch (r.type) {
      case trashBinRecordTypes.customer:
        return r.customerID;
      case trashBinRecordTypes.customerContact:
        return r.customerContactID;
      case trashBinRecordTypes.pool:
        return r.poolID;
      default:
        return null;
    }
  };

  onRestoreConfirm = (r) => {
    return evt => {
      this.setState({confirmVisible: true, recordToRestore: r});
    };
  };

  closeConfirm = () => {
    this.setState({confirmVisible: false, recordToRestore: null});
  };

  submitConfirm = () => {
    const {formatMessage} = this.props.intl;
    const {recordToRestore} = this.state;
    const recordTypeNames = trashBinRecordTypesFormat(formatMessage);

    if (recordToRestore) {
      TrashBinApi.restore(recordToRestore)
        .then(data => {
          if (data) {
            this.fetchData();
            events.publish('success.user', {
              message: formatMessage(
                {
                  id: 'trashbin.restore.success',
                },
                {
                  entity: recordTypeNames[recordToRestore.type],
                  id: this.getRecordID(recordToRestore),
                }
              ),
            });
            this.setState({confirmVisible: false, recordToRestore: null});
          }
        })
        .catch(err => {
          console.log(err);
          events.publish('error.user', {
            message: formatMessage(
              {
                id: 'trashbin.restore.error',
              },
              {
                entity: recordTypeNames[recordToRestore.type],
                id: this.getRecordID(recordToRestore),
              }
            ),
          });
          this.setState({confirmVisible: false, recordToRestore: null});
        });
    }
  };

  render() {
    const {
      recordType,
      timeframe,
      loaded,
      page,
      report,
      total,
      dealer,
    } = this.state;
    const {formatMessage} = this.props.intl;
    const recordTypeNames = trashBinRecordTypesFormat(formatMessage);
    const recordTypesOptions = this.buildDropdownOptions(
      recordTypeNames,
      true
    );
    const timeframeOptions = this.buildDropdownOptions(
      trashBinTimeFramesFormat(formatMessage)
    );
    const getRecordKey = r => {
      switch (r.type) {
        case trashBinRecordTypes.customer:
          return `${trashBinRecordTypes.customer}-${r.customerID}`;
        case trashBinRecordTypes.customerContact:
          return `${trashBinRecordTypes.customerContact}-${
            r.customerContactID
          }`;
        case trashBinRecordTypes.pool:
          return `${trashBinRecordTypes.pool}-${r.poolID}`;
        default:
          return null;
      }
    };

    return (
      <div className="container page-trashbin">
        <Header mainTitle={formatMessage({id: 'trashbin.title'})}/>
        <span className="title-description">
          <FormattedMessage id="trashbin.description" />
        </span>
        <div className="trashbin-container">
          <div className="row">
            <div className="col-sm-12">
              <div className="block-header">
                <FormattedMessage id="trashbin.filter.section.title" />
              </div>
            </div>
          </div>
          <div className="filter-section">
            <div className="row">
              <div className="col-sm-3">
                <LayoutSelect
                  label={formatMessage({id: 'trashbin.filter.record.type'})}
                  dataKey="recordType"
                  key="recordType"
                  value={recordType}
                  dataChanged={this.updateFilter}
                  editMode={true}
                  options={recordTypesOptions.labels}
                  keys={recordTypesOptions.keys}
                />
              </div>
              <div className="col-sm-3">
                <LayoutSelect
                  label={formatMessage({
                    id: 'trashbin.filter.timeframe.lookup',
                  })}
                  dataKey="timeframe"
                  key="timeframe"
                  value={timeframe}
                  dataChanged={this.updateFilter}
                  editMode={true}
                  options={timeframeOptions.labels}
                  keys={timeframeOptions.keys}
                />
              </div>
            </div>
          </div>
        </div>
        <Loader loaded={loaded}>
          <TrashBinErrorHandler error={this.state.error}>
            {report && report.length ?
              report.map(d =>
                <TrashBinRecord
                  key={getRecordKey(d)}
                  record={d}
                  dealer={dealer}
                  onRestore={this.onRestoreConfirm(d)}
                />) :
              <FormattedMessage id={'trashbin.nodata'} />
            }
            <PageFooter className="tc">
              <Pagination
                page={page}
                count={PAGE_SIZE}
                total={total}
                onPageChange={this.changePage}
              />
            </PageFooter>
          </TrashBinErrorHandler>
          <ConfirmationModal
            dismissKey="TRASHBIN_RESTORE_DISMISS"
            message={this.state.recordToRestore ?
              formatMessage({id: 'trashbin.record.restore.confirm'},  {entity: recordTypeNames[this.state.recordToRestore.type].toLowerCase()}) :
              formatMessage({id: 'trashbin.restore.confirm'})}
            submitLabel={formatMessage({id: 'general.confirm'})}
            onClose={this.closeConfirm}
            onSubmit={this.submitConfirm}
            visible={this.state.confirmVisible}
          />
        </Loader>
      </div>
    );
  }
}

let WrapperTrashBin = injectIntl(TrashBin);

export default WrapperTrashBin;
